import { getStores, hasSDK } from '@littlebird/sdk';
import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { tabStore } from '@/features/tabs/tabs.store';

export type RoutineFilter =
  | 'active'
  | 'suggestions'
  | 'paused'
  | 'experimental'
  | 'daily'
  | 'weekly'
  | 'monthly';

type RoutineCreateStep = 'pick' | 'configure';

export type RoutineCreateFlowState = {
  sourceKey: string;
  step: RoutineCreateStep;
  selectedTemplateId: string;
};

type RoutineTabState = {
  routineId: number | null;
  selectedReportId: number | null;
  isReportPinned: boolean;
  showHistory: boolean;
};

const DEFAULT_TAB_ID = 'default-tab';
export const getRoutineDraftOwnerId = (): string =>
  tabStore.activeTabId ?? DEFAULT_TAB_ID;

const DEFAULT_TAB_STATE: RoutineTabState = {
  routineId: null,
  selectedReportId: null,
  isReportPinned: false,
  showHistory: false,
};

class RoutinesStore {
  private tabStateByTabId = new Map<string, RoutineTabState>();
  createFlowStateByOwnerId = new Map<string, RoutineCreateFlowState>();
  routineFilters: RoutineFilter[] = [];
  searchQuery = '';

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'routineCreateFlow',
      properties: ['createFlowStateByOwnerId'],
      storage: window.localStorage,
    });
    tabStore.onClosedTabDiscarded((tab) => {
      this.tabStateByTabId.delete(tab.id);
      this.createFlowStateByOwnerId.delete(tab.id);
      if (hasSDK()) getStores().routineDrafts.clearOwner(tab.id);
    });
  }

  private get activeTabId() {
    return tabStore.activeTabId ?? DEFAULT_TAB_ID;
  }

  private get activeTabState() {
    return this.tabStateByTabId.get(this.activeTabId) ?? DEFAULT_TAB_STATE;
  }

  private updateActiveTabState(updates: Partial<RoutineTabState>) {
    this.tabStateByTabId.set(this.activeTabId, {
      ...this.activeTabState,
      ...updates,
    });
  }

  private updateActiveRoutineState(
    routineId: number,
    updates: Partial<RoutineTabState>
  ) {
    const previousRoutineId = this.activeTabState.routineId;
    const routineChanged = previousRoutineId !== routineId;

    if (routineChanged && previousRoutineId !== null && hasSDK()) {
      const { routineDrafts } = getStores();
      if (!routineDrafts.isDraftEdited(this.activeTabId, previousRoutineId)) {
        routineDrafts.clearDraft(this.activeTabId, previousRoutineId);
      }
    }

    const routineReset: Partial<RoutineTabState> = routineChanged
      ? {
          routineId,
          selectedReportId: null,
          isReportPinned: false,
        }
      : {};

    this.updateActiveTabState({ ...routineReset, ...updates });
  }

  useSelectedReportId(routineId: number | null) {
    return this.getSelectedReportId(routineId);
  }
  getSelectedReportId(routineId: number | null) {
    return this.activeTabState.routineId === routineId
      ? this.activeTabState.selectedReportId
      : null;
  }

  /**
   * A routine either follows its newest run or shows a run the user pinned by
   * opening it from history. Following is a standing state, not a snapshot of
   * whichever run happened to be newest at the time: a run that arrives later
   * takes over, and reopening the routine starts from the newest one again.
   */
  useIsReportPinned(routineId: number | null) {
    return this.getIsReportPinned(routineId);
  }
  getIsReportPinned(routineId: number | null) {
    return this.activeTabState.routineId === routineId
      ? this.activeTabState.isReportPinned
      : false;
  }
  pinReport(routineId: number, reportId: number) {
    this.showReport(routineId, reportId, true);
  }
  /** Keeps the run on screen until the newest one resolves, so nothing blanks out. */
  unpinReport(routineId: number) {
    this.updateActiveRoutineState(routineId, { isReportPinned: false });
  }
  /** Records the run the screen settled on, for the header, actions and analytics. */
  showReport(routineId: number, reportId: number | null, isPinned: boolean) {
    this.updateActiveRoutineState(routineId, {
      selectedReportId: reportId,
      isReportPinned: isPinned,
    });
  }

  useShowHistory() {
    return this.activeTabState.showHistory;
  }
  getShowHistory() {
    return this.activeTabState.showHistory;
  }
  setShowHistory(show: boolean) {
    this.updateActiveTabState({ showHistory: show });
  }

  syncSidebarRoutine(routineId: number) {
    if (this.activeTabState.routineId === routineId) return;
    this.updateActiveRoutineState(routineId, {});
  }
  showRuns() {
    this.setShowHistory(true);
  }

  // Hook naming keeps this observable read live under React Compiler
  useCreateFlowState(ownerId: string) {
    return this.createFlowStateByOwnerId.get(ownerId) ?? null;
  }
  getCreateFlowState(ownerId: string, sourceKey: string) {
    const state = this.createFlowStateByOwnerId.get(ownerId);
    return state?.sourceKey === sourceKey ? state : null;
  }
  getCreateFlowSourceKey(ownerId: string) {
    return this.createFlowStateByOwnerId.get(ownerId)?.sourceKey ?? null;
  }
  initializeCreateFlowState(
    ownerId: string,
    initialState: RoutineCreateFlowState
  ) {
    if (this.getCreateFlowState(ownerId, initialState.sourceKey)) return;
    this.createFlowStateByOwnerId.set(ownerId, initialState);
  }
  updateCreateFlowState(
    ownerId: string,
    initialState: RoutineCreateFlowState,
    updates: Partial<Omit<RoutineCreateFlowState, 'sourceKey'>>
  ) {
    const state =
      this.getCreateFlowState(ownerId, initialState.sourceKey) ?? initialState;
    this.createFlowStateByOwnerId.set(ownerId, { ...state, ...updates });
  }
  clearCreateFlowState(ownerId: string) {
    this.createFlowStateByOwnerId.delete(ownerId);
  }
  clearAllCreateFlowState() {
    this.createFlowStateByOwnerId.clear();
  }

  useRoutineFilters() {
    return this.routineFilters;
  }
  toggleRoutineFilter(filter: RoutineFilter) {
    this.routineFilters = this.routineFilters.includes(filter)
      ? this.routineFilters.filter((current) => current !== filter)
      : [...this.routineFilters, filter];
  }
  clearRoutineFilters() {
    this.routineFilters = [];
  }

  useSearchQuery() {
    return this.searchQuery;
  }
  setSearchQuery(query: string) {
    this.searchQuery = query;
  }
}

export const routinesStore = new RoutinesStore();
